import Icon from "@react-native-vector-icons/material-design-icons/static";
import { memo, useMemo, useState } from "react";
import isEqual from "react-fast-compare";
import {
	Appbar,
	IconButton,
	Menu,
	ProgressBar,
	Text,
} from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import Container from "../../Container";
import type { QuestionsConfig } from "../StudySessionBottomAppbar/NavButtons";

type Props = {
	isSummativeTest?: boolean;
	// Formative
	showClose?: boolean;
	handleSelectClose?: () => void;
	isAnswered?: boolean;
	currentProgress?: number;
	isReportPage?: boolean;
	fullWidth?: boolean;
	// Summative
	showMenuIcon?: boolean;
	handleClickMenuItem?: (type: "pause" | "help") => void;
	timeRemaining?: string;
	blocks?: { currentProgress?: number }[];
	currentBlockIndex?: number;
	numTotalBlocks?: number;
	partProgressLabel?: string;
	numCurrentSessionItems?: number;
	inReviewMode?: boolean;
	currentItemIndex?: number;
	questionsMenuConfig?: QuestionsConfig;
};

const RenderBlocks = ({
	blocks,
	titleText,
	blockProgress,
	inReview,
	isMobile,
	timeRemaining,
	showProgressLabel,
}: {
	blocks: { currentProgress?: number }[];
	titleText: string;
	blockProgress: string;
	inReview?: boolean;
	isMobile?: boolean;
	timeRemaining?: string;
	showProgressLabel?: boolean;
}) => {
	const theme = useAppTheme();

	return (
		<Box flex={1} alignItems="center" gap={8}>
			{isMobile && !inReview ? (
				<Text
					variant="titleMedium"
					style={{ paddingBottom: 4, color: theme.colors.white }}
				>
					{timeRemaining}
				</Text>
			) : (
				<Text variant="titleMedium" style={{ color: theme.colors.white }}>
					{titleText}
				</Text>
			)}
			<Box flexDirection="row" gap={8} width="100%">
				{blocks.map(({ currentProgress }, index) => (
					<Box key={index.toString()} flex={1}>
						<ProgressBar
							progress={currentProgress || 0}
							color={theme.palette.info.main}
							aria-label="Progress bar"
							style={{
								height: 4,
								borderRadius: 4,
								width: null,
								backgroundColor: theme.colors.white,
							}}
						/>
					</Box>
				))}
			</Box>
			{!inReview && showProgressLabel ? (
				<Text variant="labelSmall" style={{ color: theme.colors.white }}>
					{blockProgress}
				</Text>
			) : (
				<Box width={27} height={27} />
			)}
		</Box>
	);
};

const StudySessionProgressbar = ({
	isSummativeTest,
	showClose,
	handleSelectClose,
	isAnswered,
	currentProgress = 0,
	isReportPage = false,
	fullWidth,
	showMenuIcon,
	handleClickMenuItem,
	timeRemaining,
	blocks = [],
	currentBlockIndex = 0,
	numTotalBlocks = 1,
	partProgressLabel,
	numCurrentSessionItems = 0,
	inReviewMode,
	currentItemIndex = 0,
	questionsMenuConfig,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const [menuVisible, setMenuVisible] = useState(false);

	const EPSILON = Number.EPSILON * 100;
	const isProgressComplete = isReportPage
		? Math.abs(1 - currentProgress) <= EPSILON
		: Math.abs(1 - currentProgress) <= EPSILON && isAnswered;
	const progressBoxBackgroundColor = isProgressComplete
		? theme.colors.primary
		: theme.colors.surfaceVariant;

	const titleText = `${currentItemIndex} of ${numCurrentSessionItems}`;
	const blockProgress =
		partProgressLabel ??
		`Section ${currentBlockIndex + 1} of ${numTotalBlocks}`;
	const showProgressLabel = partProgressLabel ? true : blocks.length > 1;

	const pauseActionIcon = inReviewMode
		? "check-circle-outline"
		: "pause-circle-outline";
	const pauseActionLabel = inReviewMode ? "End Review" : "Pause Session";

	const jumpToQuestionTitle = useMemo(() => {
		if (!questionsMenuConfig) {
			return "";
		}
		const n = questionsMenuConfig.activeFilterCount ?? 0;
		const base = questionsMenuConfig.label;
		if (n <= 0) {
			return base;
		}
		const suffix = n > 9 ? "9+" : String(n);
		return `${base} (${suffix})`;
	}, [questionsMenuConfig]);

	return (
		<Container maxWidth={fullWidth ? "lg" : "md"} fullWidth={fullWidth}>
			<Box flexDirection="row" alignItems="center">
				{showClose && (
					<IconButton
						onPress={handleSelectClose}
						iconColor={
							isSummativeTest
								? theme.colors.white
								: theme.colors.onSurfaceVariant
						}
						style={{ marginLeft: -10 }}
						icon={(props) => <Icon {...props} name="close" />}
					/>
				)}
				{isSummativeTest ? (
					<RenderBlocks
						blocks={blocks}
						titleText={titleText}
						blockProgress={blockProgress}
						inReview={inReviewMode}
						isMobile={isMobile}
						timeRemaining={timeRemaining}
						showProgressLabel={showProgressLabel}
					/>
				) : (
					<Box flex={1} position="relative">
						<ProgressBar
							aria-label="Progress bar"
							progress={isReportPage ? 1 : currentProgress}
							style={{ height: 8, borderRadius: 4 }}
						/>
					</Box>
				)}
				{showMenuIcon && (
					<Menu
						visible={menuVisible}
						onDismiss={() => setMenuVisible(false)}
						anchor={
							<Appbar.Action
								color={
									isSummativeTest
										? theme.colors.white
										: theme.colors.onSurfaceVariant
								}
								icon="dots-vertical"
								onPress={() => setMenuVisible(true)}
							/>
						}
					>
						{questionsMenuConfig && jumpToQuestionTitle ? (
							<Menu.Item
								leadingIcon="view-list-outline"
								title={jumpToQuestionTitle}
								onPress={() => {
									setMenuVisible(false);
									questionsMenuConfig.onPress();
								}}
							/>
						) : null}
						<Menu.Item
							leadingIcon={pauseActionIcon}
							title={pauseActionLabel}
							onPress={() => {
								setMenuVisible(false);
								handleClickMenuItem?.("pause");
							}}
						/>
						<Menu.Item
							leadingIcon="help-circle-outline"
							title="Help"
							onPress={() => {
								setMenuVisible(false);
								handleClickMenuItem?.("help");
							}}
						/>
					</Menu>
				)}
				{!isSummativeTest && (
					<Box
						backgroundColor={progressBoxBackgroundColor}
						width={44}
						height={44}
						borderRadius={22}
						justifyContent="center"
						alignItems="center"
						style={{ marginLeft: -3, zIndex: 1000 }}
					>
						<Icon size={24} name="check" color={theme.colors.white} />
					</Box>
				)}
			</Box>
		</Container>
	);
};

export default memo(StudySessionProgressbar, isEqual);
